<template>
  <div class="inline-camera-capture">
    <!-- Nút mở camera nếu chưa có URL -->
    <button
      v-if="!modelValue"
      type="button"
      class="btn btn-outline-primary btn-sm custom-btn"
      :disabled="disabled"
      @click="openCamera"
    >
      <i class="fa fa-camera mr-1"></i>
      <span>{{
  buttonText
}}</span>

    </button>

    <!-- Preview + Xóa nếu đã có URL -->
    <div v-else class="mb-2 text-center">
      <img :src="modelValue" alt="Ảnh" style="max-height: 160px; border-radius: 6px;">
      <div class="mt-1">
        <button type="button" class="btn btn-sm btn-link text-danger" :disabled="disabled" @click="clearImage">
          Xóa ảnh
        </button>
      </div>
    </div>

    <!-- Camera Modal -->
    <div
      v-if="state.open"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,.6);"
      @click="closeCamera"
    >
      <div class="modal-dialog modal-lg" role="document" @click.stop>
        <div class="modal-content">
          <div class="modal-header bg-light">
            <h5 class="modal-title">
              <i class="fa fa-camera mr-2"></i>{{ modalTitle }}
            </h5>
            <button type="button" class="close" @click="closeCamera"><span>&times;</span></button>
          </div>

          <div class="modal-body">
            <div v-if="state.error" class="alert alert-danger">{{ state.error }}</div>

            <!-- Live preview -->
            <div v-show="!state.capturedUrl" class="text-center">
              <video
                ref="videoEl"
                playsinline
                autoplay
                muted
                style="max-width:100%;border-radius:6px;background:#000;"
              ></video>
            </div>

            <!-- Captured preview -->
            <div v-show="state.capturedUrl" class="text-center">
              <img :src="state.capturedUrl" alt="captured" style="max-width:100%;border-radius:6px;">
            </div>
          </div>

          <div class="modal-footer d-flex justify-content-between">
            <div>
              <button
                v-if="!state.capturedUrl"
                type="button"
                class="btn btn-outline-secondary"
                @click="switchFacing"
                :disabled="state.switching"
              >
                <i class="fa fa-exchange mr-1"></i> Đổi camera
              </button>
            </div>
            <div>
              <button
                v-if="!state.capturedUrl"
                type="button"
                class="btn btn-primary"
                @click="capturePhoto"
                :disabled="!state.stream"
              >
                <i class="fa fa-camera"></i> Chụp
              </button>

              <button
                v-else
                type="button"
                class="btn btn-outline-secondary mr-2"
                @click="retake"
              >
                <i class="fa fa-undo"></i> Chụp lại
              </button>

              <button
                v-if="state.capturedBlob && !state.uploading"
                type="button"
                class="btn btn-success"
                @click="useCaptured"
              >
                <i class="fa fa-check"></i> Dùng ảnh này
              </button>

              <button
                v-if="state.uploading"
                type="button"
                class="btn btn-success"
                disabled
              >
                <span class="spinner-border spinner-border-sm"></span> Đang tải lên...
              </button>
            </div>
          </div>
        </div>
      </div>
    </div> <!-- /Camera Modal -->
  </div>
</template>

<script>
import fileService from '@/services/fileService';

export default {
  name: 'CameraCapture',
  props: {
    /**
     * v-model: URL ảnh sau khi upload
     */
    modelValue: { type: String, default: '' },

    /**
     * Thư mục lưu trên server (bắt buộc)
     * ví dụ: 'checklist', 'sos', ...
     */
    folderName: { type: String, required: true },

    /**
     * Tiêu đề modal
     */
    modalTitle: { type: String, default: 'Chụp ảnh' },

    /**
     * Nhãn nút mở camera
     */
    buttonText: { type: String, default: 'Mở camera & chụp ảnh' },

    /**
     * Tên file prefix khi upload
     */
    fileNamePrefix: { type: String, default: 'capture' },

    /**
     * Độ nén JPEG (0..1)
     */
    quality: { type: Number, default: 0.9 },

    /**
     * Facing mode mặc định: 'environment' | 'user'
     */
    facingMode: { type: String, default: 'environment' },

    /**
     * Disable toàn bộ thao tác
     */
    disabled: { type: Boolean, default: false },

    /**
     * Kích thước mong muốn (ideals)
     */
    idealWidth: { type: Number, default: 1280 },
    idealHeight: { type: Number, default: 720 },
  },
  emits: ['update:modelValue', 'uploaded'],
  data() {
    return {
      state: {
        open: false,
        stream: null,
        facingMode: this.facingMode,
        switching: false,
        error: '',
        capturedBlob: null,
        capturedUrl: '',
        uploading: false,
      },
    };
  },
  methods: {
    // Public API
    openCamera() {
      if (this.disabled) return;
      this.state.error = '';
      this.state.open = true;
      this.startStream().catch(e => this.state.error = this.humanCameraError(e));
    },
    clearImage() {
      this.$emit('update:modelValue', '');
    },

    // Stream
    async startStream() {
      this.stopStream();
      const constraints = {
        video: {
          facingMode: this.state.facingMode,
          width: { ideal: this.idealWidth },
          height: { ideal: this.idealHeight },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.state.stream = stream;
      const video = this.$refs.videoEl;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
    },
    stopStream() {
      if (this.state.stream) {
        this.state.stream.getTracks().forEach(t => t.stop());
        this.state.stream = null;
      }
    },
    humanCameraError(e) {
      const name = e?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'Truy cập camera bị từ chối. Vui lòng cấp quyền camera cho ứng dụng.';
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return 'Không tìm thấy thiết bị camera phù hợp.';
      }
      return e?.message || 'Không thể mở camera. Vui lòng thử lại.';
    },
    async switchFacing() {
      this.state.switching = true;
      this.state.facingMode = this.state.facingMode === 'environment' ? 'user' : 'environment';
      try {
        await this.startStream();
      } catch (e) {
        this.state.error = this.humanCameraError(e);
      } finally {
        this.state.switching = false;
      }
    },

    // Capture
    capturePhoto() {
      const video = this.$refs.videoEl;
      if (!video) return;
      const canvas = document.createElement('canvas');
      const w = video.videoWidth || this.idealWidth;
      const h = video.videoHeight || this.idealHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          if (this.state.capturedUrl) URL.revokeObjectURL(this.state.capturedUrl);
          this.state.capturedBlob = blob;
          this.state.capturedUrl = URL.createObjectURL(blob);
          this.stopStream();
        },
        'image/jpeg',
        this.quality
      );
    },
    retake() {
      if (this.state.capturedUrl) URL.revokeObjectURL(this.state.capturedUrl);
      this.state.capturedUrl = '';
      this.state.capturedBlob = null;
      this.startStream().catch(e => this.state.error = this.humanCameraError(e));
    },

    // Upload
    async useCaptured() {
      if (!this.state.capturedBlob) return;

      this.state.uploading = true;
      try {
        const fileName = `${this.fileNamePrefix}_${Date.now()}.jpg`;
        const formData = new FormData();
        formData.append('files', new File([this.state.capturedBlob], fileName, { type: 'image/jpeg' }));
        formData.append('folderName', this.folderName);

        const res = await fileService.upload(formData);
        const first = Array.isArray(res.data) ? res.data[0] : null;
        if (!first?.location) throw new Error('Upload không trả về URL hợp lệ');

        // cập nhật v-model + emit
        this.$emit('update:modelValue', first.location);
        this.$emit('uploaded', first.location);

        this.closeCamera();
      } catch (e) {
        console.error('[CameraCapture upload] lỗi:', e);
        alert('Tải ảnh lên thất bại. Vui lòng thử lại.');
      } finally {
        this.state.uploading = false;
      }
    },

    closeCamera() {
      this.stopStream();
      if (this.state.capturedUrl) URL.revokeObjectURL(this.state.capturedUrl);
      this.state.open = false;
      this.state.error = '';
      this.state.capturedBlob = null;
      this.state.capturedUrl = '';
      this.state.uploading = false;
      this.state.switching = false;
      // trả chế độ về props ban đầu mỗi lần đóng
      this.state.facingMode = this.facingMode;
    },
  },
  beforeUnmount() {
    this.stopStream();
    if (this.state.capturedUrl) URL.revokeObjectURL(this.state.capturedUrl);
  }
};
</script>

<style scoped>
.modal{
   overflow-y: scroll !important;
}
.modal-content{
  width: 80% !important;
}
.modal.fade.show.d-block { z-index: 1060; }
.btn-outline-primary  span, i{
  color: #fff !important;
}
.custom-btn{
  background: #2eb6f5;
}
</style>
